/**
 * Hybrid Mapping Service - Production Ready
 * Uses multiple mapping services in order of preference:
 * 1. Local database (instant, free)
 * 2. Learned locations (from previous searches)
 * 3. OpenCage API (2,500 requests/day free)
 * 4. OpenStreetMap (free, unlimited)
 * 5. Google Maps API (fallback only, paid)
 */

import googleMapsProxyService from './googleMapsProxyService';
import { PRODUCTION_CONFIG } from '../config/production.js';
import { SECURITY_CONFIG } from '../config/security.js';
import { API_LIMITS } from '../config/limits.js';
import { errorHandler } from './errorHandler.jsx';
import performanceMonitor from './performanceMonitor.js';
import minimalSecurity from './security/minimalSecurity.js';
import learnedLocationsService from './supabaseLearnedLocationsService.js';

class HybridMappingService {
  constructor() {
    // Performance optimization: Multi-tier caching system with LRU
    this.memoryCache = new Map(); // Level 1: Memory (1-5ms)
    this.localStorageCache = new Map(); // Level 2: localStorage (5-10ms)
    this.databaseCache = new Map(); // Level 3: Database cache (50-100ms)
    
    // Cache configuration with proper limits
    this.cacheConfig = {
      memory: { maxSize: 100, ttl: 5 * 60 * 1000 }, // 5 minutes
      localStorage: { maxSize: 1000, ttl: 24 * 60 * 60 * 1000 }, // 24 hours
      database: { maxSize: 5000, ttl: 60 * 60 * 1000 } // 1 hour
    };
    
    // Cache access tracking for LRU
    this.cacheAccessOrder = {
      memory: [],
      localStorage: [],
      database: []
    };
    
    // Cost reduction tracking - Load from localStorage or initialize
    this.costTracking = this.loadCostTracking();
    
    // Performance tracking - Load from localStorage or initialize
    this.performanceStats = this.loadPerformanceStats();
    
    // Load localStorage cache on startup
    this.loadLocalStorageCache();
    
    // Initialize cache cleanup
    this.initializeCacheCleanup();
    
    // Start automatic cache cleanup
    this.startCacheCleanup();
    
    // Local database of common DC area locations
    this.localDatabase = [
      // Airports
      { name: 'dulles international airport', lat: 38.9445, lng: -77.4558, address: '1 Saarinen Cir, Dulles, VA 20166, USA' },
      { name: 'dulles airport', lat: 38.9445, lng: -77.4558, address: '1 Saarinen Cir, Dulles, VA 20166, USA' },
      { name: 'iad', lat: 38.9445, lng: -77.4558, address: '1 Saarinen Cir, Dulles, VA 20166, USA' },
      { name: 'reagan national airport', lat: 38.8512, lng: -77.0402, address: 'Ronald Reagan Washington National Airport, Arlington, VA' },
      { name: 'bwi airport', lat: 39.1774, lng: -76.6684, address: 'Baltimore/Washington International Airport, Baltimore, MD' },
      
      // Major DC landmarks
      { name: 'white house', lat: 38.8977, lng: -77.0365, address: '1600 Pennsylvania Avenue NW, Washington, DC' },
      { name: 'capitol hill', lat: 38.8899, lng: -77.0091, address: 'Capitol Hill, Washington, DC' },
      { name: 'lincoln memorial', lat: 38.8893, lng: -77.0502, address: 'Lincoln Memorial, Washington, DC' },
      { name: 'washington monument', lat: 38.8895, lng: -77.0353, address: 'Washington Monument, Washington, DC' },
      { name: 'jefferson memorial', lat: 38.8814, lng: -77.0365, address: 'Jefferson Memorial, Washington, DC' },
      { name: 'martin luther king memorial', lat: 38.8863, lng: -77.0442, address: 'Martin Luther King Jr. Memorial, Washington, DC' },
      
      // Major hotels
      { name: 'willard hotel', lat: 38.8969, lng: -77.0308, address: 'The Willard InterContinental, 1401 Pennsylvania Ave NW, Washington, DC' },
      { name: 'hay adams hotel', lat: 38.9006, lng: -77.0365, address: 'The Hay-Adams, 800 16th St NW, Washington, DC' },
      { name: 'watergate hotel', lat: 38.8991, lng: -77.0531, address: 'The Watergate Hotel, 2650 Virginia Ave NW, Washington, DC' },
      { name: 'mandarin oriental', lat: 38.8847, lng: -77.0164, address: 'Mandarin Oriental Washington DC, 1330 Maryland Ave SW, Washington, DC' },
      
      // Convention centers
      { name: 'washington convention center', lat: 38.8596, lng: -77.0231, address: 'Walter E. Washington Convention Center, 801 Mt Vernon Pl NW, Washington, DC' },
      { name: 'gaylord national resort', lat: 38.7826, lng: -77.0192, address: 'Gaylord National Resort & Convention Center, 201 Waterfront St, National Harbor, MD' },
      
      // Major areas
      { name: 'georgetown', lat: 38.9097, lng: -77.0654, address: 'Georgetown, Washington, DC' },
      { name: 'dupont circle', lat: 38.9096, lng: -77.0434, address: 'Dupont Circle, Washington, DC' },
      { name: 'adams morgan', lat: 38.9219, lng: -77.0425, address: 'Adams Morgan, Washington, DC' },
      { name: 'shaw', lat: 38.9120, lng: -77.0214, address: 'Shaw, Washington, DC' },
      { name: 'logan circle', lat: 38.9096, lng: -77.0297, address: 'Logan Circle, Washington, DC' },
      
      // Maryland locations
      { name: 'bethesda', lat: 38.9848, lng: -77.0947, address: 'Bethesda, MD' },
      { name: 'silver spring', lat: 38.9907, lng: -77.0261, address: 'Silver Spring, MD' },
      { name: 'rockville', lat: 39.0840, lng: -77.1528, address: 'Rockville, MD' },
      { name: 'gaithersburg', lat: 39.1434, lng: -77.2014, address: 'Gaithersburg, MD' },
      { name: 'college park', lat: 38.9979, lng: -76.9297, address: 'College Park, MD' },
      
      // Virginia locations
      { name: 'arlington', lat: 38.8816, lng: -77.0910, address: 'Arlington, VA' },
      { name: 'alexandria', lat: 38.8048, lng: -77.0469, address: 'Alexandria, VA' },
      { name: 'fairfax', lat: 38.8462, lng: -77.3064, address: 'Fairfax, VA' },
      { name: 'vienna', lat: 38.9012, lng: -77.2653, address: 'Vienna, VA' },
      { name: 'mclean', lat: 38.9348, lng: -77.1775, address: 'McLean, VA' },
      { name: 'tysons corner', lat: 38.9183, lng: -77.2208, address: 'Tysons Corner, VA' },
      { name: 'reston', lat: 38.9687, lng: -77.3411, address: 'Reston, VA' },
      { name: 'herndon', lat: 38.9696, lng: -77.3861, address: 'Herndon, VA' },
      { name: 'sterling', lat: 39.0067, lng: -77.4291, address: 'Sterling, VA' },
      { name: 'leesburg', lat: 39.1157, lng: -77.5636, address: 'Leesburg, VA' },
      
      // Major shopping centers
      { name: 'tysons corner center', lat: 38.9183, lng: -77.2208, address: 'Tysons Corner Center, 1961 Chain Bridge Rd, McLean, VA' },
      { name: 'pentagon city mall', lat: 38.8630, lng: -77.0594, address: 'Fashion Centre at Pentagon City, 1100 S Hayes St, Arlington, VA' },
      { name: 'potomac mills', lat: 38.6482, lng: -77.2497, address: 'Potomac Mills, 2700 Potomac Mills Cir, Woodbridge, VA' },
      
      // Universities
      { name: 'georgetown university', lat: 38.9076, lng: -77.0723, address: 'Georgetown University, 3700 O St NW, Washington, DC' },
      { name: 'george washington university', lat: 38.8995, lng: -77.0489, address: 'George Washington University, 2121 I St NW, Washington, DC' },
      { name: 'american university', lat: 38.9378, lng: -77.0889, address: 'American University, 4400 Massachusetts Ave NW, Washington, DC' },
      { name: 'university of maryland', lat: 38.9869, lng: -76.9426, address: 'University of Maryland, College Park, MD' },
      
      // Stadiums and venues
      { name: 'fedex field', lat: 38.9078, lng: -76.8644, address: 'FedExField, 1600 FedEx Way, Landover, MD' },
      { name: 'capital one arena', lat: 38.8981, lng: -77.0208, address: 'Capital One Arena, 601 F St NW, Washington, DC' },
      { name: 'nats park', lat: 38.8730, lng: -77.0074, address: 'Nationals Park, 1500 S Capitol St SE, Washington, DC' },
      { name: 'audi field', lat: 38.8701, lng: -77.0117, address: 'Audi Field, 100 Potomac Ave SW, Washington, DC' },
      
      // Metro stations (major ones)
      { name: 'union station', lat: 38.8977, lng: -77.0065, address: 'Union Station, 50 Massachusetts Ave NE, Washington, DC' },
      { name: 'metro center', lat: 38.8983, lng: -77.0281, address: 'Metro Center Station, Washington, DC' },
      { name: 'gallery place', lat: 38.8980, lng: -77.0219, address: 'Gallery Place-Chinatown Station, Washington, DC' },
      { name: 'dupont circle metro', lat: 38.9096, lng: -77.0434, address: 'Dupont Circle Station, Washington, DC' },
      { name: 'foggy bottom', lat: 38.9006, lng: -77.0503, address: 'Foggy Bottom-GWU Station, Washington, DC' },
      
      // Government buildings
      { name: 'pentagon', lat: 38.8719, lng: -77.0563, address: 'The Pentagon, Arlington, VA' },
      { name: 'fbi headquarters', lat: 38.8951, lng: -77.0364, address: 'FBI Headquarters, 935 Pennsylvania Ave NW, Washington, DC' },
      { name: 'department of state', lat: 38.8898, lng: -77.0431, address: 'U.S. Department of State, 2201 C St NW, Washington, DC' },
      
      // Common street addresses and numbers
      { name: '1600 pennsylvania', lat: 38.8977, lng: -77.0365, address: '1600 Pennsylvania Avenue NW, Washington, DC' },
      { name: '1600 pennsylvania avenue', lat: 38.8977, lng: -77.0365, address: '1600 Pennsylvania Avenue NW, Washington, DC' },
      { name: '1600 pennsylvania ave', lat: 38.8977, lng: -77.0365, address: '1600 Pennsylvania Avenue NW, Washington, DC' },
      { name: '1600 penn ave', lat: 38.8977, lng: -77.0365, address: '1600 Pennsylvania Avenue NW, Washington, DC' },
      { name: '1600 penn', lat: 38.8977, lng: -77.0365, address: '1600 Pennsylvania Avenue NW, Washington, DC' },
      
      // Common number patterns
      { name: '2827', lat: 38.8048, lng: -77.0469, address: '2827 (Alexandria area), VA' },
      { name: '2827 telek', lat: 38.8048, lng: -77.0469, address: '2827 Telek Pl, Alexandria, VA 22314' },
      { name: '2827 telek pl', lat: 38.8048, lng: -77.0469, address: '2827 Telek Pl, Alexandria, VA 22314' },
      { name: '2827 telek place', lat: 38.8048, lng: -77.0469, address: '2827 Telek Pl, Alexandria, VA 22314' },
      
      // Museums
      { name: 'smithsonian', lat: 38.8882, lng: -77.0199, address: 'Smithsonian Institution, Washington, DC' },
      { name: 'national gallery', lat: 38.8913, lng: -77.0201, address: 'National Gallery of Art, Washington, DC' },
      { name: 'holocaust museum', lat: 38.8869, lng: -77.0325, address: 'United States Holocaust Memorial Museum, Washington, DC' },
      
      // Popular restaurants/areas
      { name: 'georgetown waterfront', lat: 38.9047, lng: -77.0654, address: 'Georgetown Waterfront, Washington, DC' },
      { name: 'wharf', lat: 38.8765, lng: -77.0178, address: 'The Wharf, Washington, DC' },
      { name: 'h street corridor', lat: 38.9000, lng: -76.9900, address: 'H Street Corridor, Washington, DC' },
      { name: 'u street', lat: 38.9168, lng: -77.0281, address: 'U Street, Washington, DC' },
      
      // Suburbs and neighborhoods
      { name: 'chevy chase', lat: 38.9784, lng: -77.0765, address: 'Chevy Chase, MD' },
      { name: 'kensington', lat: 39.0254, lng: -77.0765, address: 'Kensington, MD' },
      { name: 'wheaton', lat: 39.0398, lng: -77.0553, address: 'Wheaton, MD' },
      { name: 'germantown', lat: 39.1732, lng: -77.2717, address: 'Germantown, MD' },
      { name: 'gaithersburg', lat: 39.1434, lng: -77.2014, address: 'Gaithersburg, MD' },
      { name: 'potomac', lat: 39.0182, lng: -77.2086, address: 'Potomac, MD' },
      { name: 'olney', lat: 39.1532, lng: -77.0669, address: 'Olney, MD' },
      { name: 'aspen hill', lat: 39.0790, lng: -77.0731, address: 'Aspen Hill, MD' },
      { name: 'wheaton', lat: 39.0398, lng: -77.0553, address: 'Wheaton, MD' },
      { name: 'forest glen', lat: 39.0162, lng: -77.0425, address: 'Forest Glen, MD' },
      { name: 'takoma park', lat: 38.9784, lng: -77.0075, address: 'Takoma Park, MD' },
      { name: 'hyattsville', lat: 38.9559, lng: -76.9455, address: 'Hyattsville, MD' },
      { name: 'college park', lat: 38.9979, lng: -76.9297, address: 'College Park, MD' },
      { name: 'greenbelt', lat: 39.0046, lng: -76.8758, address: 'Greenbelt, MD' },
      { name: 'beltsville', lat: 39.0348, lng: -76.9075, address: 'Beltsville, MD' },
      { name: 'laurel', lat: 39.0993, lng: -76.8483, address: 'Laurel, MD' },
      { name: 'bowie', lat: 38.9423, lng: -76.7298, address: 'Bowie, MD' },
      { name: 'upper marlboro', lat: 38.8159, lng: -76.7497, address: 'Upper Marlboro, MD' },
      { name: 'clinton', lat: 38.7651, lng: -76.8983, address: 'Clinton, MD' },
      { name: 'fort washington', lat: 38.7073, lng: -77.0230, address: 'Fort Washington, MD' },
      { name: 'oxon hill', lat: 38.8032, lng: -77.0014, address: 'Oxon Hill, MD' },
      { name: 'temple hills', lat: 38.8140, lng: -76.9455, address: 'Temple Hills, MD' },
      { name: 'suitland', lat: 38.8487, lng: -76.9236, address: 'Suitland, MD' },
      { name: 'capitol heights', lat: 38.8848, lng: -76.9128, address: 'Capitol Heights, MD' },
      { name: 'seat pleasant', lat: 38.8962, lng: -76.9061, address: 'Seat Pleasant, MD' },
      { name: 'cheverly', lat: 38.9282, lng: -76.9158, address: 'Cheverly, MD' },
      { name: 'landover', lat: 38.9340, lng: -76.8964, address: 'Landover, MD' },
      { name: 'new carrollton', lat: 38.9698, lng: -76.8797, address: 'New Carrollton, MD' },
      { name: 'riverdale', lat: 38.9634, lng: -76.9136, address: 'Riverdale, MD' },
      { name: 'mount rainier', lat: 38.9415, lng: -76.9647, address: 'Mount Rainier, MD' },
      { name: 'brentwood', lat: 38.9432, lng: -76.9564, address: 'Brentwood, MD' },
      { name: 'north brentwood', lat: 38.9455, lng: -76.9514, address: 'North Brentwood, MD' },
      { name: 'bladensburg', lat: 38.9393, lng: -76.9336, address: 'Bladensburg, MD' },
      { name: 'colmar manor', lat: 38.9332, lng: -76.9455, address: 'Colmar Manor, MD' },
      { name: 'cottage city', lat: 38.9364, lng: -76.9486, address: 'Cottage City, MD' },
      { name: 'edmonston', lat: 38.9464, lng: -76.9314, address: 'Edmonston, MD' },
      { name: 'fairmount heights', lat: 38.9006, lng: -76.9147, address: 'Fairmount Heights, MD' },
      { name: 'glenarden', lat: 38.9293, lng: -76.8614, address: 'Glenarden, MD' },
      { name: 'greenbelt', lat: 39.0046, lng: -76.8758, address: 'Greenbelt, MD' },
      { name: 'hillcrest heights', lat: 38.8329, lng: -76.9597, address: 'Hillcrest Heights, MD' },
      { name: 'landover hills', lat: 38.9432, lng: -76.8964, address: 'Landover Hills, MD' },
      { name: 'morningside', lat: 38.8232, lng: -76.8914, address: 'Morningside, MD' },
      { name: 'new carrollton', lat: 38.9698, lng: -76.8797, address: 'New Carrollton, MD' },
      { name: 'north chevy chase', lat: 38.9784, lng: -77.0765, address: 'North Chevy Chase, MD' },
      { name: 'riverdale park', lat: 38.9634, lng: -76.9136, address: 'Riverdale Park, MD' },
      { name: 'university park', lat: 38.9706, lng: -76.9514, address: 'University Park, MD' },
      { name: 'west hyattsville', lat: 38.9559, lng: -76.9455, address: 'West Hyattsville, MD' },
      
      // Virginia suburbs
      { name: 'annandale', lat: 38.8304, lng: -77.1964, address: 'Annandale, VA' },
      { name: 'burke', lat: 38.7935, lng: -77.2717, address: 'Burke, VA' },
      { name: 'centreville', lat: 38.8409, lng: -77.4289, address: 'Centreville, VA' },
      { name: 'chantilly', lat: 38.8943, lng: -77.4311, address: 'Chantilly, VA' },
      { name: 'clifton', lat: 38.7832, lng: -77.3869, address: 'Clifton, VA' },
      { name: 'dumfries', lat: 38.5676, lng: -77.3286, address: 'Dumfries, VA' },
      { name: 'fairfax station', lat: 38.8009, lng: -77.3253, address: 'Fairfax Station, VA' },
      { name: 'falls church', lat: 38.8823, lng: -77.1711, address: 'Falls Church, VA' },
      { name: 'fort belvoir', lat: 38.7149, lng: -77.1453, address: 'Fort Belvoir, VA' },
      { name: 'great falls', lat: 39.0012, lng: -77.2886, address: 'Great Falls, VA' },
      { name: 'lorton', lat: 38.7042, lng: -77.2278, address: 'Lorton, VA' },
      { name: 'manassas', lat: 38.7509, lng: -77.4753, address: 'Manassas, VA' },
      { name: 'mount vernon', lat: 38.7193, lng: -77.1073, address: 'Mount Vernon, VA' },
      { name: 'oakton', lat: 38.8809, lng: -77.3008, address: 'Oakton, VA' },
      { name: 'springfield', lat: 38.7893, lng: -77.1872, address: 'Springfield, VA' },
      { name: 'vienna', lat: 38.9012, lng: -77.2653, address: 'Vienna, VA' },
      { name: 'woodbridge', lat: 38.6582, lng: -77.2497, address: 'Woodbridge, VA' },
      { name: 'burke centre', lat: 38.7935, lng: -77.2717, address: 'Burke Centre, VA' },
      { name: 'kings park', lat: 38.8209, lng: -77.2208, address: 'Kings Park, VA' },
      { name: 'kings park west', lat: 38.8209, lng: -77.2208, address: 'Kings Park West, VA' },
      { name: 'mantua', lat: 38.8509, lng: -77.2208, address: 'Mantua, VA' },
      { name: 'merrifield', lat: 38.8743, lng: -77.2208, address: 'Merrifield, VA' },
      { name: 'west falls church', lat: 38.8823, lng: -77.1711, address: 'West Falls Church, VA' },
      { name: 'dunn loring', lat: 38.8832, lng: -77.2286, address: 'Dunn Loring, VA' },
      { name: 'pimmit hills', lat: 38.9209, lng: -77.2208, address: 'Pimmit Hills, VA' },
      { name: 'tysons', lat: 38.9183, lng: -77.2208, address: 'Tysons, VA' },
      { name: 'westgate', lat: 38.8509, lng: -77.2208, address: 'Westgate, VA' },
      { name: 'wolf trap', lat: 38.9343, lng: -77.2808, address: 'Wolf Trap, VA' },
      { name: 'hunter mill', lat: 38.8509, lng: -77.2208, address: 'Hunter Mill, VA' },
      { name: 'great falls', lat: 39.0012, lng: -77.2886, address: 'Great Falls, VA' },
      { name: 'dranesville', lat: 38.9509, lng: -77.3208, address: 'Dranesville, VA' },
      { name: 'sully', lat: 38.8509, lng: -77.4208, address: 'Sully, VA' },
      { name: 'providence', lat: 38.8209, lng: -77.3208, address: 'Providence, VA' },
      { name: 'braddock', lat: 38.8209, lng: -77.3208, address: 'Braddock, VA' },
      { name: 'mason', lat: 38.8209, lng: -77.3208, address: 'Mason, VA' },
      { name: 'lee', lat: 38.8209, lng: -77.3208, address: 'Lee, VA' },
      { name: 'mount vernon', lat: 38.7193, lng: -77.1073, address: 'Mount Vernon, VA' },
      { name: 'lee district', lat: 38.8209, lng: -77.3208, address: 'Lee District, VA' },
      { name: 'franconia', lat: 38.7509, lng: -77.1508, address: 'Franconia, VA' },
      { name: 'mount vernon district', lat: 38.7193, lng: -77.1073, address: 'Mount Vernon District, VA' },
      { name: 'sully district', lat: 38.8509, lng: -77.4208, address: 'Sully District, VA' },
      { name: 'providence district', lat: 38.8209, lng: -77.3208, address: 'Providence District, VA' },
      { name: 'braddock district', lat: 38.8209, lng: -77.3208, address: 'Braddock District, VA' },
      { name: 'mason district', lat: 38.8209, lng: -77.3208, address: 'Mason District, VA' },
      { name: 'lee district', lat: 38.8209, lng: -77.3208, address: 'Lee District, VA' },
      { name: 'dranesville district', lat: 38.9509, lng: -77.3208, address: 'Dranesville District, VA' },
      { name: 'hunter mill district', lat: 38.8509, lng: -77.2208, address: 'Hunter Mill District, VA' },
      { name: 'sully district', lat: 38.8509, lng: -77.4208, address: 'Sully District, VA' },
      { name: 'providence district', lat: 38.8209, lng: -77.3208, address: 'Providence District, VA' },
      { name: 'braddock district', lat: 38.8209, lng: -77.3208, address: 'Braddock District, VA' },
      { name: 'mason district', lat: 38.8209, lng: -77.3208, address: 'Mason District, VA' },
      { name: 'lee district', lat: 38.8209, lng: -77.3208, address: 'Lee District, VA' },
      { name: 'dranesville district', lat: 38.9509, lng: -77.3208, address: 'Dranesville District, VA' },
      { name: 'hunter mill district', lat: 38.8509, lng: -77.2208, address: 'Hunter Mill District, VA' }
    ];

    // Production configuration
    this.config = PRODUCTION_CONFIG;
    this.securityConfig = SECURITY_CONFIG;
    
    // API configuration
    this.opencageApiKey = this.config.apis.opencage.apiKey || 'bac3764f100747d4888860391e1a99a5';
    this.opencageBaseUrl = this.config.apis.opencage.baseUrl;
    
    // API key loaded and services initialized
    
    // Enhanced caches with TTL
    this.suggestionsCache = new Map();
    this.geocodingCache = new Map();
    this.routingCache = new Map();
    this.cacheTimestamps = new Map();
    this.cacheTTL = this.config.cache.suggestionsTTL;
    this.cacheHits = 0;
    this.cacheMisses = 0;
    
    // Learning system - now uses database storage
    this.maxLearnedLocations = this.config.learning.maxLearnedLocations;
    this.learnedLocationsCache = new Map(); // Cache for performance
    this.cacheLastUpdate = 0;
    this.cacheTTL = 5 * 60 * 1000; // 5 minutes
    
    // Clean expired cache entries every 10 minutes
    setInterval(() => {
      this.cleanExpiredCache();
      this.cleanupLearnedLocations();
      // Reset Google Maps proxy status to allow retry
      googleMapsProxyService.resetProxyStatus();
    }, this.config.cache.cleanupInterval);
    
    // Initialize error handling and performanceMonitor (only if enabled)
    errorHandler.init();
    if (performanceMonitor.isEnabled) {
      performanceMonitor.startMonitoring();
    }
  }

  /**
   * Load performance stats from localStorage
   * @returns {Object} Performance stats object
   */
  loadPerformanceStats() {
    try {
      const saved = localStorage.getItem('hybridMapping_performanceStats');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Ensure all required properties exist
        return {
          totalSearches: parsed.totalSearches || 0,
          avgResponseTime: parsed.avgResponseTime || 0,
          cacheHitRate: parsed.cacheHitRate || 0,
          apiCallDistribution: parsed.apiCallDistribution || {},
          costSavings: parsed.costSavings || 0,
          cacheSizes: parsed.cacheSizes || { memory: 0, localStorage: 0, database: 0 }
        };
      }
    } catch (error) {
      // Failed to load performance stats from localStorage - using defaults
    }
    
    // Return default stats if loading fails
    return {
      totalSearches: 0,
      avgResponseTime: 0,
      cacheHitRate: 0,
      apiCallDistribution: {},
      costSavings: 0,
      cacheSizes: { memory: 0, localStorage: 0, database: 0 }
    };
  }

  /**
   * Save performance stats to localStorage
   */
  savePerformanceStats() {
    try {
      localStorage.setItem('hybridMapping_performanceStats', JSON.stringify(this.performanceStats));
    } catch (error) {
      // Failed to save performance stats to localStorage - continuing without persistence
    }
  }

  /**
   * Load cost tracking from localStorage
   * @returns {Object} Cost tracking object
   */
  loadCostTracking() {
    try {
      const saved = localStorage.getItem('hybridMapping_costTracking');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Ensure all required properties exist
        return {
          totalSearches: parsed.totalSearches || 0,
          googleApiCalls: parsed.googleApiCalls || 0,
          opencageApiCalls: parsed.opencageApiCalls || 0,
          osmApiCalls: parsed.osmApiCalls || 0,
          localDbHits: parsed.localDbHits || 0,
          learnedHits: parsed.learnedHits || 0,
          cacheHits: parsed.cacheHits || { memory: 0, localStorage: 0, database: 0 },
          dailyGoogleLimit: parsed.dailyGoogleLimit || API_LIMITS.GOOGLE_DAILY_LIMIT,
          monthlyBudget: parsed.monthlyBudget || API_LIMITS.GOOGLE_MONTHLY_BUDGET
        };
      }
    } catch (error) {
      // Failed to load cost tracking from localStorage - using defaults
    }
    
    // Return default cost tracking if loading fails
    return {
      totalSearches: 0,
      googleApiCalls: 0,
      opencageApiCalls: 0,
      osmApiCalls: 0,
      localDbHits: 0,
      learnedHits: 0,
      cacheHits: { memory: 0, localStorage: 0, database: 0 },
      dailyGoogleLimit: API_LIMITS.GOOGLE_DAILY_LIMIT,
      monthlyBudget: API_LIMITS.GOOGLE_MONTHLY_BUDGET
    };
  }

  /**
   * Save cost tracking to localStorage
   */
  saveCostTracking() {
    try {
      localStorage.setItem('hybridMapping_costTracking', JSON.stringify(this.costTracking));
    } catch (error) {
      // Failed to save cost tracking to localStorage - continuing without persistence
    }
  }

  /**
   * Load localStorage cache on startup
   */
  loadLocalStorageCache() {
    try {
      const saved = localStorage.getItem('addressSuggestionsCache');
      if (saved) {
        const cacheData = JSON.parse(saved);
        if (Array.isArray(cacheData)) {
          // Restore the localStorage cache
          this.localStorageCache.clear();
          cacheData.forEach(([key, value]) => {
            this.localStorageCache.set(key, value);
          });
        }
      }
    } catch (error) {
      // Failed to load localStorage cache - starting with empty cache
    }
  }

  /**
   * Search local database for address matches (optimized)
   * @param {string} query - Search query
   * @returns {Object|null} Matching location or null
   */
  searchLocalDatabase(query) {
    const normalizedQuery = query.toLowerCase().trim();
    
    // Exact match first
    let match = this.localDatabase.find(location => 
      location.name === normalizedQuery
    );
    
    if (match) {
      return {
        lat: match.lat,
        lng: match.lng,
        formatted_address: match.address,
        place_id: `local_${match.name.replace(/\s+/g, '_')}`,
        source: 'local'
      };
    }
    
    // Partial match
    match = this.localDatabase.find(location => 
      location.name.includes(normalizedQuery) || 
      normalizedQuery.includes(location.name)
    );
    
    if (match) {
      return {
        lat: match.lat,
        lng: match.lng,
        formatted_address: match.address,
        place_id: `local_${match.name.replace(/\s+/g, '_')}`,
        source: 'local'
      };
    }
    
    return null;
  }

  /**
   * Optimized local database search with early termination and better scoring
   * @param {string} query - Search query
   * @param {number} maxResults - Maximum number of results to return
   * @returns {Array} Array of matching suggestions
   */
  searchLocalDatabaseOptimized(query, maxResults = 8) {
    const normalizedQuery = query.toLowerCase().trim();
    const suggestions = [];
    const queryWords = normalizedQuery.split(/\s+/);
    
    // First pass: exact matches and high-confidence partial matches
    for (const location of this.localDatabase) {
      const locationName = location.name.toLowerCase();
      let score = 0;
      
      // Exact match gets highest score
      if (locationName === normalizedQuery) {
        score = 1.0;
      }
      // Starts with query gets high score
      else if (locationName.startsWith(normalizedQuery)) {
        score = 0.9;
      }
      // Contains query gets medium score
      else if (locationName.includes(normalizedQuery)) {
        score = 0.7;
      }
      // Word-based matching
      else {
        const locationWords = locationName.split(/\s+/);
        const matchingWords = queryWords.filter(qWord => 
          locationWords.some(lWord => lWord.includes(qWord) || qWord.includes(lWord))
        );
        score = matchingWords.length / queryWords.length * 0.6;
      }
      
      // Special handling for number patterns (like "2827")
      if (normalizedQuery.match(/^\d+$/)) {
        const numberMatch = locationName.match(/\d+/);
        if (numberMatch && numberMatch[0].startsWith(normalizedQuery)) {
          score = Math.max(score, 0.8);
        }
      }
      
      if (score > 0.3) { // Only include results with decent similarity
        suggestions.push({
          lat: location.lat,
          lng: location.lng,
          description: location.address,
          formatted_address: location.address,
          place_id: `local_${location.name.replace(/\s+/g, '_')}`,
          score: score,
          source: 'local'
        });
      }
      
      // Early termination if we have enough high-quality results
      if (suggestions.length >= maxResults && suggestions[0].score > 0.8) {
        break;
      }
    }
    
    // Sort by score and return top results
    return suggestions
      .sort((a, b) => b.score - a.score)
      .slice(0, maxResults);
  }

  /**
   * Places-first suggestions for estimate/booking forms.
   * Uses Google Places Autocomplete + Details; falls back to Geocoding.
   * Records Google results to learned_locations.
   */
  async getGooglePlacesSuggestions(query, countryCode = 'US', clientIP = 'unknown') {
    if (!query || query.length < this.config.performance.minQueryLength) {
      return [];
    }

    const securityResult = minimalSecurity.fastSecurityCheck(query, 'query', clientIP);
    const sanitizedQuery = securityResult?.sanitized || query;
    const maxSuggestions = this.config.performance.maxSuggestions || 8;

    try {
      let suggestions = await googleMapsProxyService.getPlaceSuggestionsWithDetails(
        sanitizedQuery,
        countryCode
      );

      // Fallback to Google Geocoding (still Google API) if Places empty
      if (!suggestions.length) {
        const geo = await googleMapsProxyService.getAddressSuggestions(sanitizedQuery, countryCode);
        suggestions = (geo || []).map((s, index) => ({
          ...s,
          score: 0.9 - index * 0.05,
          source: 'google',
        }));
      }

      if (suggestions.length > 0) {
        // Fire-and-forget — never let learning wipe the dropdown
        void this.learnFromApiResults(suggestions, sanitizedQuery).catch((err) => {
          console.warn('learnFromApiResults failed (suggestions still returned):', err?.message || err);
        });
      }

      return suggestions.slice(0, maxSuggestions);
    } catch (error) {
      console.error('getGooglePlacesSuggestions failed:', error);
      return [];
    }
  }

  /**
   * Persist a user-selected suggestion to learned_locations.
   */
  async recordSelectedSuggestion(suggestion, originalQuery = '') {
    if (!suggestion) return;
    if (typeof suggestion.lat !== 'number' || typeof suggestion.lng !== 'number') return;

    const withMeta = {
      ...suggestion,
      source: suggestion.source || 'google',
      score: suggestion.score ?? 0.95,
    };

    const query =
      originalQuery ||
      suggestion.description ||
      suggestion.formatted_address ||
      '';

    try {
      await this.addLearnedLocation(withMeta, query);
    } catch (error) {
      console.error('recordSelectedSuggestion failed:', error);
    }
  }

  /**
   * Get address suggestions using optimized hybrid approach
   * @param {string} query - Search query
   * @param {string} countryCode - Country code (default: 'US')
   * @returns {Promise<Array>} Array of address suggestions
   */
  async getAddressSuggestions(query, countryCode = 'US', clientIP = 'unknown') {
    const startTime = performance.now();
    
    // Quick validation first (performance optimized)
    if (!query || query.length < this.config.performance.minQueryLength) {
      return [];
    }

    // Minimal security validation (proceed with sanitized input instead of aborting)
    const securityResult = minimalSecurity.fastSecurityCheck(query, 'query', clientIP);
    const sanitizedQuery = securityResult?.sanitized || query;
    if (!securityResult?.isValid) {
      // Track but continue to attempt suggestions using sanitized input
      performanceMonitor.trackError('security_validation_warn', 'address_suggestions', {
        query: minimalSecurity.maskSensitiveData(query),
        threats: securityResult?.threats || [],
        clientIP,
        performance: securityResult?.performance
      });
    }
    const cacheKey = `${sanitizedQuery.toLowerCase().trim()}-${countryCode}`;
    
    // Check multi-tier cache first
    const cachedResult = this.getCachedSuggestionsOptimized(cacheKey);
    if (cachedResult) {
      this.cacheHits++;
      performanceMonitor.trackCache(true, 'suggestions');
      
      // Track performance
      const duration = performance.now() - startTime;
      this.updatePerformanceStats(duration);
      this.updateCostTracking(cachedResult); // Track cache hits
      performanceMonitor.trackSearch(query, duration, { source: 'cache' });
      
      return cachedResult;
    }
    
    this.cacheMisses++;
    performanceMonitor.trackCache(false, 'suggestions');

    const suggestions = [];
    const maxSuggestions = this.config.performance.maxSuggestions;
    const minScore = 0.2;

    // 1. Try local database first (instant, free) - optimized search
    const localResults = this.searchLocalDatabaseOptimized(sanitizedQuery, maxSuggestions);
    suggestions.push(...localResults);

    // 2. Try learned locations from previous API searches
    const learnedResults = await this.searchLearnedLocations(sanitizedQuery, maxSuggestions);
    suggestions.push(...learnedResults);
    
    // Update usage statistics for learned locations
    if (learnedResults.length > 0) {
      this.updateLearnedLocationUsage(query);
    }

    // Prefer Google geocoding for route-critical queries (airports, city names)
    const isRouteCritical = /airport|washington|downtown|\bdc\b|\biad\b|\bdca\b|\bbwi\b/i.test(sanitizedQuery);
    if (isRouteCritical) {
      const googleResults = await this.fetchGoogleMapsSuggestions(sanitizedQuery, countryCode);
      if (googleResults.length > 0) {
        suggestions.push(...googleResults);
      }
    }

    // If we have enough high-quality local results, return early
    if (!isRouteCritical &&
        suggestions.length >= this.config.performance.earlyReturnThreshold && 
        suggestions[0].score > this.config.performance.earlyReturnScore) {
      const finalSuggestions = this.removeDuplicateSuggestions(suggestions).slice(0, maxSuggestions);
      this.cacheSuggestionsOptimized(cacheKey, finalSuggestions);
      
      // Track performance
      const duration = performance.now() - startTime;
      this.updatePerformanceStats(duration);
      this.updateCostTracking(finalSuggestions); // Track local database hits
      performanceMonitor.trackSearch(query, duration, { source: 'local_early_return' });
      
      return finalSuggestions;
    }

    // 3. Make parallel API requests for better performance
    const apiPromises = [];
    
    // OpenCage API (opt-in via env)
    if (
      this.opencageApiKey &&
      typeof this.opencageApiKey === 'string' &&
      !/your_opencage_api_key_here/i.test(this.opencageApiKey) &&
      import.meta.env.VITE_ENABLE_OPENCAGE === 'true'
    ) {
      apiPromises.push(this.fetchOpenCageSuggestions(sanitizedQuery));
    }

    // OpenStreetMap (free, unlimited)
    apiPromises.push(this.fetchOpenStreetMapSuggestions(sanitizedQuery));

    // Google Maps API via proxy (fallback) - always try as fallback
    apiPromises.push(this.fetchGoogleMapsSuggestions(sanitizedQuery, countryCode));

    // Wait for all API calls to complete (with timeout)
    try {
      const apiResults = await Promise.allSettled(apiPromises);
      
      // Process successful results and learn from them
      for (const [index, result] of apiResults.entries()) {
        if (result.status === 'fulfilled' && result.value && result.value.length > 0) {
          suggestions.push(...result.value);
          
          // Learn from API results - save to local database
          await this.learnFromApiResults(result.value, query);
        }
      }
    } catch (error) {
      // Some API requests failed - continuing with available results
    }

    // 3. Final fallback - return a generic suggestion if nothing found
    if (suggestions.length === 0) {
      suggestions.push({
        place_id: 'fallback_1',
        description: `Search for "${query}" in Washington DC area`,
        formatted_address: `Search for "${query}" in Washington DC area`,
        lat: 38.9072,
        lng: -77.0369,
        // Ensure fallback passes minScore filter
        score: 0.3,
        source: 'fallback'
      });
    }

    // Remove duplicates, sort by score, and return top results
    const uniqueSuggestions = this.removeDuplicateSuggestions(suggestions);
    const finalSuggestions = uniqueSuggestions
      .filter(s => s.score >= minScore)
      .sort((a, b) => b.score - a.score)
      .slice(0, maxSuggestions);
    
    this.cacheSuggestionsOptimized(cacheKey, finalSuggestions);
    
    // Track performance and update stats
    const duration = performance.now() - startTime;
    this.updatePerformanceStats(duration);
    this.updateCostTracking(finalSuggestions); // Track API usage
    performanceMonitor.trackSearch(query, duration, { source: 'api' });
    
    return finalSuggestions;
  }

  /**
   * Geocode an address using hybrid approach
   * @param {string} address - Address to geocode
   * @returns {Promise<Object|null>} Geocoded result with lat/lng
   */
  async geocodeAddress(address) {
    if (this.geocodingCache.has(address)) {
      return this.geocodingCache.get(address);
    }

    // 1. Try local database first
    const localResult = this.searchLocalDatabase(address);
    if (localResult) {
      this.geocodingCache.set(address, localResult);
      return localResult;
    }

    // 2. Try OpenCage API (only if a real key is configured)
    if (this.opencageApiKey && typeof this.opencageApiKey === 'string' && !/your_opencage_api_key_here/i.test(this.opencageApiKey)) {
      try {
        const url = `${this.opencageBaseUrl}?q=${encodeURIComponent(address)}&key=${this.opencageApiKey}&countrycode=us&limit=1&no_annotations=1`;
        const response = await fetch(url);
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.results && data.results.length > 0) {
            const result = data.results[0];
            const geocodedResult = {
              lat: result.geometry.lat,
              lng: result.geometry.lng,
              formatted_address: result.formatted,
              place_id: `opencage_${result.annotations.geohash}`,
              source: 'opencage'
            };
            
            this.geocodingCache.set(address, geocodedResult);
            return geocodedResult;
          }
        }
      } catch (error) {
        // Silent error handling
      }
    }

    // 3. Try OpenStreetMap
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1&countrycodes=us&addressdetails=1`;
      const response = await fetch(url, {
        headers: {
          'User-Agent': 'Akiblackcar/1.0'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data && data.length > 0) {
          const result = data[0];
          const geocodedResult = {
            lat: parseFloat(result.lat),
            lng: parseFloat(result.lon),
            formatted_address: result.display_name,
            place_id: `osm_${result.place_id}`,
            source: 'openstreetmap'
          };
          
          this.geocodingCache.set(address, geocodedResult);
          return geocodedResult;
        }
      }
    } catch (error) {
      // Silent error handling
    }

    // 4. Fallback to Google Maps API via proxy
    try {
      const googleResult = await googleMapsProxyService.geocodeAddress(address);
      if (googleResult) {
        this.geocodingCache.set(address, googleResult);
        return googleResult;
      }
    } catch (error) {
      // Silent error handling
    }

    return null;
  }

  /**
   * Calculate route distance using hybrid approach
   * @param {Object} origin - Origin coordinates {lat, lng}
   * @param {Object} destination - Destination coordinates {lat, lng}
   * @returns {Promise<Object>} Route information with distance and duration
   */
  async calculateRouteDistance(origin, destination) {
    const originKey = origin.formatted_address || origin.description || origin.address || `${origin.lat},${origin.lng}`;
    const destKey = destination.formatted_address || destination.description || destination.address || `${destination.lat},${destination.lng}`;
    const cacheKey = `${originKey}|${destKey}`;
    
    if (this.routingCache.has(cacheKey)) {
      return this.routingCache.get(cacheKey);
    }

    // 1. Try Google Maps API via proxy first (most accurate)
    try {
      const googleResult = await googleMapsProxyService.calculateRouteDistance(origin, destination);
      if (googleResult && googleResult.source === 'google') {
        this.routingCache.set(cacheKey, googleResult);
        return googleResult;
      }
    } catch (error) {
      // Silent error handling
    }

    // 2. Fallback to straight-line distance calculation
    const result = this.calculateStraightLineDistance(origin, destination);
    this.routingCache.set(cacheKey, result);
    return result;
  }

  /**
   * Calculate straight-line distance as fallback
   * @param {Object} origin - Origin coordinates {lat, lng}
   * @param {Object} destination - Destination coordinates {lat, lng}
   * @returns {Object} Distance information
   */
  calculateStraightLineDistance(origin, destination) {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRadians(destination.lat - origin.lat);
    const dLng = this.toRadians(destination.lng - origin.lng);
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(this.toRadians(origin.lat)) * Math.cos(this.toRadians(destination.lat)) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    
    return {
      distance: distance,
      duration: distance * 2, // Rough estimate: 30 mph average
      distanceText: `${distance.toFixed(1)} mi`,
      durationText: `${Math.round(distance * 2)} min`,
      source: 'straight-line'
    };
  }

  /**
   * Convert degrees to radians
   * @param {number} degrees - Degrees to convert
   * @returns {number} Radians
   */
  toRadians(degrees) {
    return degrees * (Math.PI / 180);
  }

  /**
   * Fetch OpenCage suggestions
   * @param {string} query - Search query
   * @returns {Promise<Array>} Array of suggestions
   */
  async fetchOpenCageSuggestions(query) {
    const startTime = performance.now();
    
    try {
      const url = `${this.opencageBaseUrl}?q=${encodeURIComponent(query)}&key=${this.opencageApiKey}&countrycode=us&limit=5&no_annotations=1`;
      
      const response = await Promise.race([
        fetch(url),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('OpenCage API timeout')), this.config.apis.opencage.timeout)
        )
      ]);
      
      if (!response.ok) {
        throw new Error(`OpenCage API error: ${response.status}`);
      }
      
      const data = await response.json();
      const duration = performance.now() - startTime;
      
      if (data.results && data.results.length > 0) {
        performanceMonitor.trackApiCall('opencage', duration, true);
        
        return data.results.map((item, index) => ({
          place_id: `opencage_${item.annotations.geohash}`,
          description: item.formatted,
          formatted_address: item.formatted,
          lat: item.geometry.lat,
          lng: item.geometry.lng,
          score: 0.9 - (index * 0.1),
          source: 'opencage'
        }));
      }
      
      performanceMonitor.trackApiCall('opencage', duration, true);
      return [];
    } catch (error) {
      const duration = performance.now() - startTime;
      performanceMonitor.trackApiCall('opencage', duration, false);
      performanceMonitor.trackError('api_error', 'opencage', { query, error: error.message });
      
      return await errorHandler.handleApiError(error, 'OpenCage API', {
        apiName: 'opencage',
        maxRetries: this.config.errorHandling.maxRetries,
        retryDelay: this.config.errorHandling.retryDelay,
        fallback: []
      });
    }
  }

  /**
   * Fetch OpenStreetMap suggestions
   * @param {string} query - Search query
   * @returns {Promise<Array>} Array of suggestions
   */
  async fetchOpenStreetMapSuggestions(query) {
    const startTime = performance.now();
    
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&countrycodes=us&addressdetails=1`;
      
      const response = await Promise.race([
        fetch(url, {
          headers: {
            'User-Agent': 'Akiblackcar/1.0'
          }
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('OpenStreetMap API timeout')), this.config.apis.openStreetMap.timeout)
        )
      ]);
      
      if (!response.ok) {
        throw new Error(`OpenStreetMap API error: ${response.status}`);
      }
      
      const data = await response.json();
      const duration = performance.now() - startTime;
      
      if (data && data.length > 0) {
        performanceMonitor.trackApiCall('openstreetmap', duration, true);
        
        return data.map((item, index) => ({
          place_id: `osm_${item.place_id}`,
          description: item.display_name,
          formatted_address: item.display_name,
          lat: parseFloat(item.lat),
          lng: parseFloat(item.lon),
          score: 0.7 - (index * 0.1),
          source: 'osm'
        }));
      }
      
      performanceMonitor.trackApiCall('openstreetmap', duration, true);
      return [];
    } catch (error) {
      const duration = performance.now() - startTime;
      performanceMonitor.trackApiCall('openstreetmap', duration, false);
      console.warn('OpenStreetMap API failed:', error);
    }
    return [];
  }

  /**
   * Fetch Google Maps suggestions via proxy
   * @param {string} query - Search query
   * @param {string} countryCode - Country code
   * @returns {Promise<Array>} Array of suggestions
   */
  async fetchGoogleMapsSuggestions(query, countryCode) {
    try {
      const googleSuggestions = await googleMapsProxyService.getAddressSuggestions(query, countryCode);
      if (googleSuggestions && googleSuggestions.length > 0) {
        return googleSuggestions.map(s => ({
          ...s,
          score: 0.5,
          source: 'google'
        }));
      }
    } catch (error) {
      console.warn('Google Maps API failed:', error);
    }
    return [];
  }

  /**
   * Remove duplicate suggestions based on coordinates
   * @param {Array} suggestions - Array of suggestions
   * @returns {Array} Unique suggestions
   */
  removeDuplicateSuggestions(suggestions) {
    const seen = new Set();
    return suggestions.filter(suggestion => {
      const key = `${suggestion.lat},${suggestion.lng}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  /**
   * Check if cache entry is valid (not expired)
   * @param {string} cacheKey - Cache key to check
   * @returns {boolean} True if cache is valid
   */
  isCacheValid(cacheKey) {
    // Check if any of the multi-tier caches have this key
    const hasInAnyCache = this.memoryCache.has(cacheKey) || 
                         this.localStorageCache.has(cacheKey) || 
                         this.databaseCache.has(cacheKey);
    
    if (!hasInAnyCache) return false;
    
    const timestamp = this.cacheTimestamps.get(cacheKey);
    if (!timestamp) return false;
    
    return (Date.now() - timestamp) < this.cacheTTL;
  }

  /**
   * Set cache with timestamp for TTL management
   * @param {string} cacheKey - Cache key
   * @param {any} value - Value to cache
   */
  setCacheWithTTL(cacheKey, value) {
    this.suggestionsCache.set(cacheKey, value);
    this.cacheTimestamps.set(cacheKey, Date.now());
  }

  /**
   * Clean expired cache entries
   */
  cleanExpiredCache() {
    const now = Date.now();
    for (const [key, timestamp] of this.cacheTimestamps.entries()) {
      if (now - timestamp > this.cacheTTL) {
        this.suggestionsCache.delete(key);
        this.cacheTimestamps.delete(key);
      }
    }
  }

  /**
   * Search learned locations from database
   * @param {string} query - Search query
   * @param {number} maxResults - Maximum number of results
   * @returns {Promise<Array>} Array of learned location suggestions
   */
  async searchLearnedLocations(query, maxResults = 8) {
    try {
      const normalizedQuery = query.toLowerCase().trim();
      
      // Check cache first
      const cacheKey = `learned_${normalizedQuery}`;
      const now = Date.now();
      
      if (this.learnedLocationsCache.has(cacheKey) && 
          (now - this.cacheLastUpdate) < this.cacheTTL) {
        return this.learnedLocationsCache.get(cacheKey);
      }
      
      // Search database
      const dbResults = await learnedLocationsService.searchLearnedLocations(normalizedQuery, maxResults);
      
      const suggestions = dbResults.map(location => ({
        lat: parseFloat(location.latitude),
        lng: parseFloat(location.longitude),
        description: location.address,
        formatted_address: location.address,
        place_id: `learned_${location.search_key}`,
        score: 0.9, // High score for learned locations
        source: 'learned',
        search_count: location.search_count,
        last_searched: location.last_searched_at
      }));
      
      // Cache the results
      this.learnedLocationsCache.set(cacheKey, suggestions);
      this.cacheLastUpdate = now;
      
      return suggestions;
    } catch (error) {
      console.error('Error searching learned locations:', error);
      return [];
    }
  }

  /**
   * Learn from API results and save to local database
   * @param {Array} apiResults - Results from API calls
   * @param {string} originalQuery - Original search query
   */
  async learnFromApiResults(apiResults, originalQuery) {
    if (!apiResults || apiResults.length === 0) return;
    
    // Only learn from high-quality results (Google Maps, OpenCage)
    const qualityResults = apiResults.filter(result => 
      result.source === 'google' || result.source === 'opencage'
    );
    
    for (const result of qualityResults) {
      if (this.shouldLearnLocation(result, originalQuery)) {
        await this.addLearnedLocation(result, originalQuery);
      }
    }
  }

  /**
   * Determine if a location should be learned
   * @param {Object} result - API result
   * @param {string} originalQuery - Original search query
   * @returns {boolean} True if should be learned
   */
  shouldLearnLocation(result, originalQuery) {
    // Don't learn if we already have too many locations
    if (this.learnedLocationsCache.size >= this.maxLearnedLocations) {
      return false;
    }
    
    // Don't learn fallback or low-quality results
    if (result.source === 'fallback' || result.score < 0.5) {
      return false;
    }
    
    // Don't learn if it's too similar to existing learned location
    const normalizedAddress = this.normalizeAddress(result.formatted_address || result.description);
    if (this.isDuplicateLocation(normalizedAddress, result.lat, result.lng)) {
      return false;
    }
    
    return true;
  }

  /**
   * Add a learned location to the database
   * @param {Object} result - API result
   * @param {string} originalQuery - Original search query
   */
  async addLearnedLocation(result, originalQuery) {
    const normalizedAddress = this.normalizeAddress(result.formatted_address || result.description);
    const searchTerms = this.generateSearchTerms(originalQuery, result);
    
    // Create location entry
    // Save to database
    try {
      const locationData = {
        searchKey: searchTerms[0], // Primary search key
        name: searchTerms.join(' '),
        address: result.formatted_address || result.description,
        latitude: result.lat,
        longitude: result.lng,
        source: result.source
      };
      
      await learnedLocationsService.saveLearnedLocation(locationData);
      // Learned new location successfully
    } catch (error) {
      console.error('Error saving learned location to database:', error);
    }
  }

  /**
   * Normalize address for consistent storage
   * @param {string} address - Address to normalize
   * @returns {string} Normalized address
   */
  normalizeAddress(address) {
    if (!address || typeof address !== 'string') {
      return '';
    }
    return address
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/[^\w\s]/g, '');
  }

  /**
   * Generate search terms for a location
   * @param {string} originalQuery - Original search query
   * @param {Object} result - API result
   * @returns {Array} Array of search terms
   */
  generateSearchTerms(originalQuery, result) {
    if (!originalQuery || typeof originalQuery !== 'string') {
      return [];
    }
    
    const terms = [originalQuery.toLowerCase().trim()];
    const address = result?.formatted_address || result?.description;
    
    if (!address || typeof address !== 'string') {
      return terms;
    }
    
    // Extract key terms from address
    const addressWords = address.toLowerCase().split(/\s+/);
    const keyWords = addressWords.filter(word => 
      word.length > 2 && 
      !['street', 'avenue', 'road', 'drive', 'lane', 'way', 'blvd', 'st', 'ave', 'rd', 'dr', 'ln'].includes(word)
    );
    
    // Add first few key words
    terms.push(...keyWords.slice(0, 3));
    
    // Add city/state if available
    if (address.includes(',')) {
      const parts = address.split(',');
      if (parts.length >= 2) {
        terms.push(parts[1].trim().toLowerCase());
      }
    }
    
    return [...new Set(terms)]; // Remove duplicates
  }

  /**
   * Check if location is duplicate based on coordinates and address similarity
   * @param {string} normalizedAddress - Normalized address
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @returns {boolean} True if duplicate
   */
  isDuplicateLocation(normalizedAddress, lat, lng) {
    const COORDINATE_THRESHOLD = 0.001; // ~100 meters
    const ADDRESS_SIMILARITY_THRESHOLD = 0.8;
    
    if (!normalizedAddress || typeof lat !== 'number' || typeof lng !== 'number') {
      return false;
    }
    
    for (const [key, location] of this.learnedLocationsCache.entries()) {
      if (!location || typeof location.lat !== 'number' || typeof location.lng !== 'number') {
        continue;
      }
      
      // Check coordinate proximity
      const distance = Math.sqrt(
        Math.pow(location.lat - lat, 2) + Math.pow(location.lng - lng, 2)
      );
      
      if (distance < COORDINATE_THRESHOLD) {
        return true;
      }
      
      // Check address similarity
      const existingAddress = this.normalizeAddress(location.address);
      const similarity = this.calculateStringSimilarity(normalizedAddress, existingAddress);
      
      if (similarity > ADDRESS_SIMILARITY_THRESHOLD) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Calculate string similarity (Jaccard similarity)
   * @param {string} str1 - First string
   * @param {string} str2 - Second string
   * @returns {number} Similarity score (0-1)
   */
  calculateStringSimilarity(str1, str2) {
    if (!str1 || !str2 || typeof str1 !== 'string' || typeof str2 !== 'string') {
      return 0;
    }
    
    const words1 = new Set(str1.split(/\s+/));
    const words2 = new Set(str2.split(/\s+/));
    
    const intersection = new Set([...words1].filter(x => words2.has(x)));
    const union = new Set([...words1, ...words2]);
    
    return union.size > 0 ? intersection.size / union.size : 0;
  }

  /**
   * Get cache statistics for performanceMonitor
   * @returns {Object} Cache statistics
   */
  getCacheStats() {
    return {
      suggestionsCacheSize: this.suggestionsCache.size,
      geocodingCacheSize: this.geocodingCache.size,
      routingCacheSize: this.routingCache.size,
      totalCacheEntries: this.cacheTimestamps.size,
      learnedLocationsCacheSize: this.learnedLocationsCache.size,
      cacheHitRate: this.cacheHits / (this.cacheHits + this.cacheMisses) || 0
    };
  }

  /**
   * Clean up old learned locations in database
   */
  async cleanupLearnedLocations() {
    try {
      const cleanedCount = await learnedLocationsService.cleanupOldLocations();
      if (cleanedCount > 0) {
        // Cleaned up old learned locations from database
      }
    } catch (error) {
      console.error('Error cleaning up learned locations:', error);
    }
  }

  /**
   * Update search count for a learned location
   * @param {string} query - Search query that matched
   */
  async updateLearnedLocationUsage(query) {
    try {
      const normalizedQuery = query.toLowerCase().trim();
      const location = await learnedLocationsService.getLearnedLocation(normalizedQuery);
      
      if (location) {
        // The search count is automatically updated in the database
        // when we call searchLearnedLocations, so no additional action needed
        // Updated usage for learned location
      }
    } catch (error) {
      console.error('Error updating learned location usage:', error);
    }
  }

  /**
   * Get learned locations statistics
   * @returns {Promise<Object>} Statistics about learned locations
   */
  async getLearnedLocationsStats() {
    try {
      const stats = await learnedLocationsService.getLearnedLocationsStats();
      return {
        ...stats,
        cacheSize: this.learnedLocationsCache.size,
        cacheLastUpdate: this.cacheLastUpdate
      };
    } catch (error) {
      console.error('Error getting learned locations stats:', error);
      return {
        total_locations: 0,
        active_locations: 0,
        inactive_locations: 0,
        avg_search_count: 0,
        max_search_count: 0,
        locations_learned_this_week: 0,
        locations_searched_today: 0,
        cacheSize: this.learnedLocationsCache.size,
        cacheLastUpdate: this.cacheLastUpdate
      };
    }
  }

  /**
   * Clear all caches and learned locations
   */
  clearCache() {
    this.suggestionsCache.clear();
    this.geocodingCache.clear();
    this.routingCache.clear();
    this.cacheTimestamps.clear();
    this.learnedLocationsCache.clear();
    
    // Clear multi-tier caches
    this.memoryCache.clear();
    this.localStorageCache.clear();
    this.databaseCache.clear();
    
    this.cacheHits = 0;
    this.cacheMisses = 0;
  }

  /**
   * Clear only learned locations (keep built-in local database)
   */
  async clearLearnedLocations() {
    try {
      await learnedLocationsService.clearAllLearnedLocations();
      this.learnedLocationsCache.clear();
      // Cleared all learned locations from database
    } catch (error) {
      console.error('Error clearing learned locations:', error);
    }
  }

  // ==================== OPTIMIZED PERFORMANCE METHODS ====================

  /**
   * OPTIMIZED: Multi-tier cache lookup
   */
  getCachedSuggestionsOptimized(cacheKey) {
    // Check TTL first
    if (!this.isCacheValid(cacheKey)) {
      return null;
    }
    
    // Level 1: Memory cache (fastest)
    if (this.memoryCache.has(cacheKey)) {
      this.costTracking.cacheHits.memory++;
      return this.memoryCache.get(cacheKey);
    }
    
    // Level 2: localStorage cache (fast)
    if (this.localStorageCache.has(cacheKey)) {
      this.costTracking.cacheHits.localStorage++;
      const result = this.localStorageCache.get(cacheKey);
      // Promote to memory cache
      this.setMemoryCache(cacheKey, result);
      return result;
    }
    
    // Level 3: Database cache (medium)
    if (this.databaseCache.has(cacheKey)) {
      this.costTracking.cacheHits.database++;
      const result = this.databaseCache.get(cacheKey);
      // Promote to higher caches
      this.setLocalStorageCache(cacheKey, result);
      this.setMemoryCache(cacheKey, result);
      return result;
    }
    
    return null;
  }

  /**
   * OPTIMIZED: Multi-tier cache storage
   */
  cacheSuggestionsOptimized(cacheKey, suggestions) {
    this.setMemoryCache(cacheKey, suggestions);
    this.setLocalStorageCache(cacheKey, suggestions);
    this.setDatabaseCache(cacheKey, suggestions);
    
    // Set timestamp for TTL management
    this.cacheTimestamps.set(cacheKey, Date.now());
  }

  /**
   * OPTIMIZED: Search learned locations with multi-tier caching
   */
  async searchLearnedLocationsOptimized(query, maxResults = 8) {
    const cacheKey = `learned_${query.toLowerCase()}`;
    
    // Check cache first
    const cached = this.getCachedSuggestionsOptimized(cacheKey);
    if (cached) return cached;
    
    try {
      const dbResults = await learnedLocationsService.searchLearnedLocations(query, maxResults);
      const suggestions = this.formatLearnedSuggestions(dbResults);
      
      // Cache results
      this.cacheSuggestionsOptimized(cacheKey, suggestions);
      
      return suggestions;
    } catch (error) {
      console.error('Error searching learned locations:', error);
      return [];
    }
  }

  /**
   * OPTIMIZED: Search external APIs with cost control
   */
  async searchExternalAPIsOptimized(query, maxResults = 8) {
    const apiPromises = [];
    
    // Check daily Google API limit - always try as fallback
    if (this.costTracking.googleApiCalls < this.costTracking.dailyGoogleLimit) {
      apiPromises.push(this.fetchGoogleMapsSuggestions(query, 'US'));
    }
    
    // Always try free APIs first
    apiPromises.push(this.fetchOpenStreetMapSuggestions(query));
    
    // OpenCage if available
    if (this.opencageApiKey) {
      apiPromises.push(this.fetchOpenCageSuggestions(query));
    }
    
    // Race condition: Return first successful result
    const results = await Promise.allSettled(apiPromises);
    
    // Combine successful results
    const allResults = results
      .filter(result => result.status === 'fulfilled')
      .flatMap(result => result.value)
      .slice(0, maxResults);

    return allResults;
  }

  /**
   * OPTIMIZED: Cache management methods
   */
  setMemoryCache(key, value) {
    if (this.memoryCache.size >= this.cacheConfig.memory.maxSize) {
      // LRU eviction
      const firstKey = this.memoryCache.keys().next().value;
      this.memoryCache.delete(firstKey);
    }
    this.memoryCache.set(key, value);
  }

  setLocalStorageCache(key, value) {
    try {
      if (this.localStorageCache.size >= this.cacheConfig.localStorage.maxSize) {
        this.evictLeastFrequentLocalStorage();
      }
      this.localStorageCache.set(key, value);
      
      // Persist to localStorage
      const cacheData = Array.from(this.localStorageCache.entries());
      localStorage.setItem('addressSuggestionsCache', JSON.stringify(cacheData));
    } catch (error) {
      console.warn('localStorage cache error:', error);
    }
  }

  setDatabaseCache(key, value) {
    if (this.databaseCache.size >= this.cacheConfig.database.maxSize) {
      this.evictOldDatabaseCache();
    }
    this.databaseCache.set(key, value);
  }

  /**
   * OPTIMIZED: Cost tracking and control
   */
  updateCostTracking(results) {
    // Increment total searches counter
    this.costTracking.totalSearches++;
    
    results.forEach(result => {
      switch (result.source) {
        case 'local':
          this.costTracking.localDbHits++;
          break;
        case 'learned':
          this.costTracking.learnedHits++;
          break;
        case 'google':
          this.costTracking.googleApiCalls++;
          break;
        case 'opencage':
          this.costTracking.opencageApiCalls++;
          break;
        case 'osm':
          this.costTracking.osmApiCalls++;
          break;
      }
    });
    
    // Save to localStorage for persistence
    this.saveCostTracking();
  }

  updateCacheHitStats(level) {
    this.costTracking.cacheHits[level]++;
  }

  /**
   * OPTIMIZED: Performance statistics
   */
  updatePerformanceStats(responseTime, results) {
    // Increment total searches counter
    this.performanceStats.totalSearches++;
    this.costTracking.totalSearches++;
    
    // Update average response time
    if (this.performanceStats.totalSearches === 1) {
      this.performanceStats.avgResponseTime = responseTime;
    } else {
      this.performanceStats.avgResponseTime = 
        (this.performanceStats.avgResponseTime * (this.performanceStats.totalSearches - 1) + responseTime) 
        / this.performanceStats.totalSearches;
    }
    
    // Update cache hit rate (avoid division by zero)
    if (this.costTracking.totalSearches > 0) {
      this.performanceStats.cacheHitRate = 
        (this.costTracking.cacheHits.memory + this.costTracking.cacheHits.localStorage + this.costTracking.cacheHits.database) 
        / this.costTracking.totalSearches;
    } else {
      this.performanceStats.cacheHitRate = 0;
    }
    
    // Calculate cost savings
    const googleCost = this.costTracking.googleApiCalls * 0.00283; // $2.83 per 1k requests
    const potentialCost = this.costTracking.totalSearches * 0.00283;
    this.performanceStats.costSavings = potentialCost - googleCost;
    
    // Update cache sizes with real-time data
    this.performanceStats.cacheSizes = {
      memory: this.memoryCache.size,
      localStorage: this.localStorageCache.size,
      database: this.databaseCache.size
    };
    
    
    // Save to localStorage for persistence
    this.savePerformanceStats();
    this.saveCostTracking();
  }

  /**
   * Get comprehensive performance and cost statistics
   */
  getOptimizedStats() {
    return {
      performance: {
        totalSearches: this.performanceStats.totalSearches,
        avgResponseTime: Math.round(this.performanceStats.avgResponseTime * 100) / 100,
        cacheHitRate: Math.round(this.performanceStats.cacheHitRate * 100) / 100,
        cacheSizes: {
          memory: this.memoryCache.size,
          localStorage: this.localStorageCache.size,
          database: this.databaseCache.size
        }
      },
      cost: {
        totalSearches: this.costTracking.totalSearches,
        googleApiCalls: this.costTracking.googleApiCalls,
        opencageApiCalls: this.costTracking.opencageApiCalls,
        osmApiCalls: this.costTracking.osmApiCalls,
        localDbHits: this.costTracking.localDbHits,
        learnedHits: this.costTracking.learnedHits,
        costSavings: Math.round(this.performanceStats.costSavings * 100) / 100,
        dailyGoogleLimit: this.costTracking.dailyGoogleLimit,
        monthlyBudget: this.costTracking.monthlyBudget
      },
      cache: {
        memory: this.costTracking.cacheHits.memory,
        localStorage: this.costTracking.cacheHits.localStorage,
        database: this.costTracking.cacheHits.database
      }
    };
  }

  // ==================== HELPER METHODS ====================

  formatLearnedSuggestions(dbResults) {
    return dbResults.map(location => ({
      lat: parseFloat(location.latitude),
      lng: parseFloat(location.longitude),
      description: location.address,
      formatted_address: location.address,
      place_id: `learned_${location.search_key}`,
      score: 0.9,
      source: 'learned',
      search_count: location.search_count,
      last_searched: location.last_searched_at
    }));
  }

  evictLeastFrequentLocalStorage() {
    // Simple eviction - remove oldest entries
    const entries = Array.from(this.localStorageCache.entries());
    const toRemove = entries.slice(0, Math.floor(entries.length * 0.1)); // Remove 10%
    toRemove.forEach(([key]) => this.localStorageCache.delete(key));
  }

  evictOldDatabaseCache() {
    // Remove entries older than TTL
    const now = Date.now();
    for (const [key, value] of this.databaseCache.entries()) {
      if (now - value.timestamp > this.cacheConfig.database.ttl) {
        this.databaseCache.delete(key);
      }
    }
  }

  /**
   * Validate search input with security checks (performance optimized)
   * @param {string} query - Search query
   * @param {string} clientIP - Client IP address
   * @returns {Object} Validation result
   */
  /**
   * Validate search input with comprehensive security checks
   * Performs multi-layer validation for maximum security and performance
   * 
   * Validation layers:
   * 1. Length check (fastest - < 0.1ms)
   * 2. Quick threat detection (fast - < 0.5ms)
   * 3. Full validation (thorough - < 2ms)
   * 
   * @param {string} query - Search query to validate
   * @param {string} clientIP - Client IP address for logging
   * @returns {Object} Validation result with security analysis
   * @returns {boolean} returns.isValid - Whether input is safe to process
   * @returns {Array<string>} returns.threats - Detected threat types
   * @returns {string} returns.sanitized - Sanitized version of input
   * @returns {Array<string>} returns.warnings - Security warnings
   * @example
   * // Safe input
   * validateSearchInput("Washington DC", "192.168.1.1")
   * // Returns: { isValid: true, threats: [], sanitized: "Washington DC", warnings: [] }
   * 
   * // Malicious input
   * validateSearchInput("<script>alert('xss')</script>", "192.168.1.1")
   * // Returns: { isValid: false, threats: ["xss_attempt"], sanitized: "alertxss", warnings: [] }
   */
  validateSearchInput(query, clientIP) {
    // Quick length check first (fastest)
    if (query.length > this.securityConfig.input.query.maxLength) {
      return {
        isValid: false,
        threats: ['input_too_long'],
        sanitized: query.substring(0, this.securityConfig.input.query.maxLength)
      };
    }

    // Fast pattern check for obvious threats (performance optimized)
    const quickThreats = this.quickThreatCheck(query);
    if (quickThreats.length > 0) {
      return {
        isValid: false,
        threats: quickThreats,
        sanitized: this.basicSanitize(query)
      };
    }

    // Full validation only if quick check passes
    const fullValidation = inputValidator.validateInput(query, 'query');
    
    return {
      isValid: fullValidation.isValid,
      threats: fullValidation.threats,
      sanitized: fullValidation.sanitized,
      warnings: fullValidation.warnings
    };
  }

  /**
   * Quick threat check for performance (checks most common attacks first)
   * @param {string} query - Query to check
   * @returns {Array} Detected threats
   */
  quickThreatCheck(query) {
    const threats = [];
    
    // Check for most common SQL injection patterns (fastest checks first)
    if (/[';]/.test(query) || /union\s+select/i.test(query) || /or\s+1\s*=\s*1/i.test(query)) {
      threats.push('sql_injection');
    }
    
    // Check for XSS patterns
    if (/<script/i.test(query) || /javascript:/i.test(query) || /on\w+\s*=/i.test(query)) {
      threats.push('xss');
    }
    
    // Check for path traversal
    if (/\.\.\//.test(query) || /\.\.\\/.test(query)) {
      threats.push('path_traversal');
    }
    
    // Check for command injection
    if (/[;&|`$()]/.test(query) || /rm\s+-rf/i.test(query) || /del\s+\/s/i.test(query)) {
      threats.push('command_injection');
    }
    
    return threats;
  }

  /**
   * Basic sanitization for performance
   * Removes potentially dangerous characters and limits input length
   * 
   * Security patterns removed:
   * - HTML/XML characters: < > ' " & (prevents XSS)
   * - Command injection characters: ; & | ` $ ( ) (prevents command injection)
   * - Path traversal: ../ (prevents directory traversal attacks)
   * 
   * @param {string} query - Query to sanitize
   * @returns {string} Sanitized query with dangerous characters removed
   * @example
   * // Input: "test<script>alert('xss')</script>"
   * // Output: "testalertxss"
   */
  basicSanitize(query) {
    return query
      .replace(/[<>'"&]/g, '') // Remove HTML/XML characters
      .replace(/[;&|`$()]/g, '') // Remove command injection characters
      .replace(/\.\.\//g, '') // Remove path traversal
      .substring(0, this.securityConfig.input.query.maxLength)
      .trim();
  }

  /**
   * Add item to cache with LRU management
   * @param {string} cacheType - Type of cache (memory, localStorage, database)
   * @param {string} key - Cache key
   * @param {any} value - Cache value
   * @param {number} ttl - Time to live in milliseconds
   */
  addToCache(cacheType, key, value, ttl) {
    const cache = this[`${cacheType}Cache`];
    const config = this.cacheConfig[cacheType];
    
    // Check if cache is full
    if (cache.size >= config.maxSize) {
      this.evictLRU(cacheType);
    }
    
    // Add to cache with timestamp
    cache.set(key, {
      value,
      timestamp: Date.now(),
      ttl: ttl || config.ttl
    });
    
    // Update access order
    this.updateAccessOrder(cacheType, key);
  }

  /**
   * Get item from cache with LRU management
   * @param {string} cacheType - Type of cache
   * @param {string} key - Cache key
   * @returns {any|null} Cached value or null
   */
  getFromCache(cacheType, key) {
    const cache = this[`${cacheType}Cache`];
    const item = cache.get(key);
    
    if (!item) return null;
    
    // Check if expired
    if (Date.now() - item.timestamp > item.ttl) {
      cache.delete(key);
      this.removeFromAccessOrder(cacheType, key);
      return null;
    }
    
    // Update access order
    this.updateAccessOrder(cacheType, key);
    return item.value;
  }

  /**
   * Update access order for LRU
   * @param {string} cacheType - Type of cache
   * @param {string} key - Cache key
   */
  updateAccessOrder(cacheType, key) {
    const accessOrder = this.cacheAccessOrder[cacheType];
    
    // Remove key if it exists
    const index = accessOrder.indexOf(key);
    if (index > -1) {
      accessOrder.splice(index, 1);
    }
    
    // Add to end (most recently used)
    accessOrder.push(key);
  }

  /**
   * Remove key from access order
   * @param {string} cacheType - Type of cache
   * @param {string} key - Cache key
   */
  removeFromAccessOrder(cacheType, key) {
    const accessOrder = this.cacheAccessOrder[cacheType];
    const index = accessOrder.indexOf(key);
    if (index > -1) {
      accessOrder.splice(index, 1);
    }
  }

  /**
   * Evict least recently used item from cache
   * @param {string} cacheType - Type of cache
   */
  evictLRU(cacheType) {
    const cache = this[`${cacheType}Cache`];
    const accessOrder = this.cacheAccessOrder[cacheType];
    
    if (accessOrder.length > 0) {
      const lruKey = accessOrder[0];
      cache.delete(lruKey);
      accessOrder.shift();
    }
  }

  /**
   * Clean expired items from cache
   * @param {string} cacheType - Type of cache
   */
  cleanExpiredItems(cacheType) {
    const cache = this[`${cacheType}Cache`];
    const now = Date.now();
    
    for (const [key, item] of cache.entries()) {
      if (now - item.timestamp > item.ttl) {
        cache.delete(key);
        this.removeFromAccessOrder(cacheType, key);
      }
    }
  }

  /**
   * Clear all caches
   */
  clearAllCaches() {
    this.memoryCache.clear();
    this.localStorageCache.clear();
    this.databaseCache.clear();
    
    this.cacheAccessOrder.memory = [];
    this.cacheAccessOrder.localStorage = [];
    this.cacheAccessOrder.database = [];
  }

  /**
   * Start automatic cache cleanup
   */
  startCacheCleanup() {
    // Clean up expired items every 5 minutes
    this.cleanupInterval = setInterval(() => {
      this.performCacheCleanup();
    }, 5 * 60 * 1000); // 5 minutes
  }

  /**
   * Stop automatic cache cleanup
   */
  stopCacheCleanup() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }

  /**
   * Perform comprehensive cache cleanup
   */
  performCacheCleanup() {
    try {
      // Clean expired items from all caches
      this.cleanExpiredItems('memory');
      this.cleanExpiredItems('localStorage');
      this.cleanExpiredItems('database');
      
      // Monitor cache sizes and clean if needed
      this.monitorCacheSizes();
      
      // Log cleanup statistics
      if (import.meta.env.DEV) {
        console.log('Cache cleanup completed:', this.getCacheStats());
      }
    } catch (error) {
      console.error('Cache cleanup error:', error);
    }
  }

  /**
   * Monitor cache sizes and clean if needed
   */
  monitorCacheSizes() {
    const memorySize = this.memoryCache.size;
    const localStorageSize = this.localStorageCache.size;
    const databaseSize = this.databaseCache.size;
    
    // Clean memory cache if it's getting too large
    if (memorySize > this.cacheConfig.memory.maxSize * 0.9) {
      this.cleanupMemoryCache();
    }
    
    // Clean localStorage cache if it's getting too large
    if (localStorageSize > this.cacheConfig.localStorage.maxSize * 0.9) {
      this.cleanupLocalStorageCache();
    }
    
    // Clean database cache if it's getting too large
    if (databaseSize > this.cacheConfig.database.maxSize * 0.9) {
      this.cleanupDatabaseCache();
    }
  }

  /**
   * Cleanup memory cache by removing least recently used items
   */
  cleanupMemoryCache() {
    const targetSize = Math.floor(this.cacheConfig.memory.maxSize * 0.7);
    while (this.memoryCache.size > targetSize) {
      this.evictLRU('memory');
    }
  }

  /**
   * Cleanup localStorage cache by removing least recently used items
   */
  cleanupLocalStorageCache() {
    const targetSize = Math.floor(this.cacheConfig.localStorage.maxSize * 0.7);
    while (this.localStorageCache.size > targetSize) {
      this.evictLRU('localStorage');
    }
  }

  /**
   * Cleanup database cache by removing least recently used items
   */
  cleanupDatabaseCache() {
    const targetSize = Math.floor(this.cacheConfig.database.maxSize * 0.7);
    while (this.databaseCache.size > targetSize) {
      this.evictLRU('database');
    }
  }

  /**
   * Initialize cache cleanup system
   */
  initializeCacheCleanup() {
    this.cleanupInterval = null;
    this.cleanupRunning = false;
  }

  /**
   * Start automatic cache cleanup
   */
  startCacheCleanup() {
    if (this.cleanupInterval) {
      return; // Already running
    }

    // Clean up every 5 minutes
    this.cleanupInterval = setInterval(() => {
      this.performCacheCleanup();
    }, 5 * 60 * 1000);
  }

  /**
   * Stop automatic cache cleanup
   */
  stopCacheCleanup() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }

  /**
   * Perform comprehensive cache cleanup
   */
  performCacheCleanup() {
    if (this.cleanupRunning) {
      return; // Prevent overlapping cleanups
    }

    this.cleanupRunning = true;

    try {
      // Clean expired entries from all caches
      this.cleanupExpiredEntries('memory');
      this.cleanupExpiredEntries('localStorage');
      this.cleanupExpiredEntries('database');

      // Clean oversized caches
      this.cleanupOversizedCaches();

      // Log cleanup statistics
      if (import.meta.env?.DEV) {
        console.log('Cache cleanup completed:', {
          memory: this.memoryCache.size,
          localStorage: this.localStorageCache.size,
          database: this.databaseCache.size
        });
      }
    } catch (error) {
      console.error('Cache cleanup error:', error);
    } finally {
      this.cleanupRunning = false;
    }
  }

  /**
   * Clean expired entries from a specific cache
   * @param {string} cacheType - Type of cache to clean
   */
  cleanupExpiredEntries(cacheType) {
    const cache = this[`${cacheType}Cache`];
    const ttl = this.cacheConfig[cacheType].ttl;
    const now = Date.now();

    for (const [key, value] of cache.entries()) {
      if (value.timestamp && (now - value.timestamp) > ttl) {
        cache.delete(key);
        this.removeFromAccessOrder(cacheType, key);
      }
    }
  }

  /**
   * Remove key from access order tracking
   * @param {string} cacheType - Type of cache
   * @param {string} key - Key to remove
   */
  removeFromAccessOrder(cacheType, key) {
    const accessOrder = this.cacheAccessOrder[cacheType];
    const index = accessOrder.indexOf(key);
    if (index > -1) {
      accessOrder.splice(index, 1);
    }
  }

  /**
   * Clean up oversized caches to prevent memory issues
   */
  cleanupOversizedCaches() {
    const maxCacheSize = 1000; // Maximum entries per cache
    
    // Clean memory cache
    if (this.memoryCache.size > maxCacheSize) {
      const entriesToRemove = this.memoryCache.size - maxCacheSize;
      const keysToRemove = Array.from(this.memoryCache.keys()).slice(0, entriesToRemove);
      keysToRemove.forEach(key => {
        this.memoryCache.delete(key);
        this.removeFromAccessOrder('memory', key);
      });
    }
    
    // Clean localStorage cache
    if (this.localStorageCache.size > maxCacheSize) {
      const entriesToRemove = this.localStorageCache.size - maxCacheSize;
      const keysToRemove = Array.from(this.localStorageCache.keys()).slice(0, entriesToRemove);
      keysToRemove.forEach(key => {
        this.localStorageCache.delete(key);
        this.removeFromAccessOrder('localStorage', key);
      });
    }
    
    // Clean database cache
    if (this.databaseCache.size > maxCacheSize) {
      const entriesToRemove = this.databaseCache.size - maxCacheSize;
      const keysToRemove = Array.from(this.databaseCache.keys()).slice(0, entriesToRemove);
      keysToRemove.forEach(key => {
        this.databaseCache.delete(key);
        this.removeFromAccessOrder('database', key);
      });
    }
  }

  /**
   * Cleanup when service is destroyed
   */
  destroy() {
    this.stopCacheCleanup();
    this.memoryCache.clear();
    this.localStorageCache.clear();
    this.databaseCache.clear();
  }
}

// Singleton pattern implementation
let instance = null;

export function getHybridMappingService() {
  if (!instance) {
    instance = new HybridMappingService();
  }
  return instance;
}

// Export singleton instance for backward compatibility
export default getHybridMappingService();
